
(() => {
  'use strict';

  if (window.ichikaaInfiniteScrollLoaded) return;
  window.ichikaaInfiniteScrollLoaded = true;

  const GRID = '#product-grid';
  const PAGINATION = '.pagination-wrapper';

  let observer = null;
  let sentinel = null;
  let status = null;
  let currentGrid = null;
  let currentPagination = null;
  let nextUrl = null;
  let loading = false;
  let finished = false;

  const visitedPages = new Set();
  const seenProducts = new Set();

  function normalizePageUrl(url) {
    const parsed = new URL(url, window.location.href);
    parsed.hash = '';
    return parsed.href;
  }

  function getNextLink(container) {
    if (!container) return null;

    return (
      container.querySelector('a[aria-label="Next page"]') ||
      container.querySelector('.pagination__item--next[href]') ||
      Array.from(container.querySelectorAll('a[href]')).find(link =>
        /next page/i.test(
          (link.getAttribute('aria-label') || '') + ' ' +
          link.textContent
        )
      ) ||
      null
    );
  }

  function productKey(product) {
    const link = product.querySelector('a[href*="/products/"]');

    if (!link) return null;

    const url = new URL(link.href, window.location.href);
    return url.pathname;
  }

  function removeLoader() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (sentinel) {
      sentinel.remove();
      sentinel = null;
      status = null;
    }
  }

  function stopScrolling(message) {
    finished = true;
    nextUrl = null;
    removeLoader();

    if (currentPagination) {
      currentPagination.hidden = true;
    }

    console.info('Ichikaa infinite scroll stopped:', message);
  }

  function initialise() {
    const grid = document.querySelector(GRID);
    const pagination = document.querySelector(PAGINATION);

    if (!grid || !pagination) return;

    if (grid === currentGrid && pagination === currentPagination) {
      return;
    }

    removeLoader();

    currentGrid = grid;
    currentPagination = pagination;
    finished = false;
    loading = false;
    visitedPages.clear();
    seenProducts.clear();

    // Remember products already visible on the first page.
    Array.from(grid.children)
      .filter(item => item.classList.contains('grid__item'))
      .forEach(product => {
        const key = productKey(product);
        if (key) seenProducts.add(key);
      });

    const link = getNextLink(pagination);
    nextUrl = link ? normalizePageUrl(link.href) : null;

    if (!nextUrl || visitedPages.has(nextUrl)) {
      return;
    }

    sentinel = document.createElement('div');
    sentinel.className = 'ichikaa-infinite-scroll-trigger';
    sentinel.style.cssText =
      'width:100%;padding:20px;text-align:center;';

    status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = 'Scroll to load more products';

    sentinel.appendChild(status);
    pagination.insertAdjacentElement('afterend', sentinel);

    observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        loadNextPage();
      }
    }, { rootMargin: '300px 0px' });

    observer.observe(sentinel);
  }

  async function loadNextPage() {
    if (loading || finished || !nextUrl) return;

    const requestedUrl = normalizePageUrl(nextUrl);

    // Never request the same page twice.
    if (visitedPages.has(requestedUrl)) {
      stopScrolling('This page has already been visited.');
      return;
    }

    loading = true;
    visitedPages.add(requestedUrl);

    if (status) status.textContent = 'Loading more products…';

    try {
      const response = await fetch(requestedUrl, {
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('Could not load the next collection page.');
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      if (!currentGrid || !document.contains(currentGrid)) {
        stopScrolling('The product grid changed.');
        return;
      }

      const nextGrid = doc.querySelector(GRID);
      const nextPagination = doc.querySelector(PAGINATION);

      if (!nextGrid) {
        stopScrolling('No product grid found on the next page.');
        return;
      }

      const products = Array.from(nextGrid.children).filter(item =>
        item.classList.contains('grid__item')
      );

      let added = 0;

      products.forEach(product => {
        const key = productKey(product);

        // Skip products that have already appeared.
        if (key && seenProducts.has(key)) return;

        if (key) seenProducts.add(key);

        currentGrid.appendChild(product);
        added++;
      });

      const nextLink = getNextLink(nextPagination);
      const candidateUrl = nextLink
        ? normalizePageUrl(nextLink.href)
        : null;

      // Stop if there are no new products or no next page.
      if (
        added === 0 ||
        !candidateUrl ||
        visitedPages.has(candidateUrl)
      ) {
        stopScrolling(
          added === 0
            ? 'No new products were found.'
            : 'Reached the last page.'
        );
        return;
      }

      nextUrl = candidateUrl;

      if (status) {
        status.textContent = 'Scroll to load more products';
      }

    } catch (error) {
      // Allow a retry if the network request failed.
      visitedPages.delete(requestedUrl);
      console.error('Ichikaa infinite scroll:', error);

      if (status) {
        status.textContent =
          'Unable to load more products. Please refresh or use pagination.';
      }
    } finally {
      loading = false;
    }
  }

  function start() {
    initialise();

    const changes = new MutationObserver(() => {
      const grid = document.querySelector(GRID);
      const pagination = document.querySelector(PAGINATION);

      if (
        grid !== currentGrid ||
        pagination !== currentPagination
      ) {
        initialise();
      }
    });

    changes.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();
